import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Database, ExternalLink, Code } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';

const erpSystems = [
  {
    id: 'sap',
    name: 'SAP ERP',
    logo: '🔷',
    clients: 42,
    status: 'active',
    apiVersion: 'v2.1',
    endpoint: '/api/v2/sap',
    schema: {
      invoice: {
        invoiceNumber: 'string',
        date: 'ISO8601',
        customerId: 'string',
        items: 'array',
        totalAmount: 'decimal',
      }
    },
    notes: 'SAP requires OAuth 2.0 authentication. Token refresh every 3600 seconds.'
  },
  {
    id: 'oracle',
    name: 'Oracle ERP Cloud',
    logo: '🔴',
    clients: 28,
    status: 'active',
    apiVersion: 'v3.0',
    endpoint: '/api/v3/oracle',
    schema: {
      invoice: {
        docNumber: 'string',
        invoiceDate: 'YYYY-MM-DD',
        buyer: 'object',
        lineItems: 'array',
        netAmount: 'number',
      }
    },
    notes: 'Oracle uses REST API with Basic Auth. Rate limit: 1000 requests/hour.'
  },
  {
    id: 'tally',
    name: 'Tally Prime',
    logo: '🟡',
    clients: 35,
    status: 'active',
    apiVersion: 'v1.5',
    endpoint: '/api/v1/tally',
    schema: {
      voucher: {
        voucherNumber: 'string',
        voucherDate: 'DD-MM-YYYY',
        partyName: 'string',
        ledgerEntries: 'array',
        amount: 'number',
      }
    },
    notes: 'Tally uses XML format. Requires TDL configuration on client side.'
  },
  {
    id: 'zoho',
    name: 'Zoho Books',
    logo: '🟣',
    clients: 22,
    status: 'active',
    apiVersion: 'v2.0',
    endpoint: '/api/v2/zoho',
    schema: {
      invoice: {
        invoice_id: 'string',
        date: 'YYYY-MM-DD',
        customer_id: 'string',
        line_items: 'array',
        total: 'float',
      }
    },
    notes: 'Zoho uses API key authentication. Supports webhooks for real-time sync.'
  },
  {
    id: 'dynamics',
    name: 'Microsoft Dynamics 365',
    logo: '🔵',
    clients: 20,
    status: 'active',
    apiVersion: 'v4.0',
    endpoint: '/api/v4/dynamics',
    schema: {
      salesOrder: {
        orderNumber: 'string',
        orderDate: 'ISO8601',
        accountId: 'guid',
        orderLines: 'array',
        totalAmount: 'decimal',
      }
    },
    notes: 'Dynamics 365 uses OAuth with Microsoft Azure AD. Supports OData queries.'
  },
];

export default function ERPMasters() {
  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl text-gray-900">ERP Masters</h1>
        <p className="text-gray-600 mt-1">Manage ERP system configurations and API schemas</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total ERP Systems</p>
                <p className="text-2xl text-gray-900 mt-2">5</p>
              </div>
              <Database className="w-10 h-10 text-[#0052CC]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Integrations</p>
                <p className="text-2xl text-gray-900 mt-2">147</p>
              </div>
              <ExternalLink className="w-10 h-10 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">API Endpoints</p>
                <p className="text-2xl text-gray-900 mt-2">5</p>
              </div>
              <Code className="w-10 h-10 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ERP Systems List */}
      <Card>
        <CardHeader>
          <CardTitle>Supported ERP Systems</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {erpSystems.map((erp) => (
              <AccordionItem key={erp.id} value={erp.id}>
                <AccordionTrigger>
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex items-center space-x-4">
                      <span className="text-2xl">{erp.logo}</span>
                      <div className="text-left">
                        <p className="text-gray-900">{erp.name}</p>
                        <p className="text-sm text-gray-500">API Version: {erp.apiVersion}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Badge variant="secondary">{erp.clients} clients</Badge>
                      <Badge variant="default" className="bg-green-500">Active</Badge>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-700">
                        <span className="text-gray-900">API Endpoint:</span> {erp.endpoint}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-900 mb-2">JSON Schema Reference:</p>
                      <pre className="bg-gray-900 text-green-400 p-4 rounded text-xs overflow-x-auto">
{JSON.stringify(erp.schema, null, 2)}
                      </pre>
                    </div>

                    <div>
                      <p className="text-sm text-gray-900">Integration Notes:</p>
                      <p className="text-sm text-gray-700 mt-1">{erp.notes}</p>
                    </div>

                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm">
                        <Code className="w-4 h-4 mr-2" />
                        View Full Documentation
                      </Button>
                      <Button variant="outline" size="sm">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Test Connection
                      </Button>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}
